<script setup lang="ts">
  import type { FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { Button } from '@/components/ui/button'
  import { Checkbox } from '@/components/ui/checkbox'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Input } from '@/components/ui/input'
  import { Label } from '@/components/ui/label'
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
  } from '@/components/ui/select'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { useToast } from '@/components/ui/toast'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { downloadDocument } from '@/lib/utils'
  import { ROUTES } from '@/router/routes'
  import { OrganisationsService, StaleDocumentsService } from '@/types/generated/services.gen'
  import { type TDocument, type TOrganisation } from '@/types/generated/types.gen'
  import { AlertCircle, CheckCircle, Copy, Download, RefreshCw, Search, X } from 'lucide-vue-next'
  import { computed, onMounted, ref, watch } from 'vue'

  const { toast } = useToast()
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  // Filter states
  const timeRangeFilter = ref('24')
  const orgFilter = ref('all')
  const staleThresholdMinutes = ref('15')
  const pageSize = ref('20')
  const showErrored = ref('false')

  // Organizations for filters
  const organizations = ref<TOrganisation[]>([])
  const orgSearchTerm = ref('')

  // Selection state
  const selectedDocuments = ref<Set<string>>(new Set())
  const isSelectAll = ref(false)
  const currentPageItems = ref<TDocument[]>([])

  // Confirmation dialog state
  const showConfirmDialog = ref(false)
  const confirmAction = ref<() => Promise<void>>(() => Promise.resolve())
  const confirmTitle = ref('')
  const confirmDescription = ref('')

  // Store all fetched data for client-side filtering
  const allStaleDocuments = ref<any[]>([])
  const isLoadingData = ref(false)

  const fetchStaleDocuments = async (
    options: FetchDataOptions = { limit: 20, offset: 0, search: '' }
  ) => {
    // Only fetch from API if we don't have data yet or filters changed
    if (allStaleDocuments.value.length === 0 || isLoadingData.value) {
      isLoadingData.value = true

      const params: any = {
        limit: 1000, // Fetch more data for client-side filtering
        offset: 0
      }

      // Add time filter
      params.hours = timeRangeFilter.value

      // Add organization filter
      if (orgFilter.value && orgFilter.value !== 'all') {
        params.orgId = orgFilter.value
      }

      // Add stale threshold (only if not showing errored documents)
      if (showErrored.value !== 'true') {
        params.staleThresholdMinutes = parseInt(staleThresholdMinutes.value)
      }

      // Add show errored filter
      params.showErrored = showErrored.value === 'true'

      const result = await StaleDocumentsService.staleDocumentsList(params)
      allStaleDocuments.value = result.results || []
      isLoadingData.value = false
    }

    // Apply client-side search filtering
    let filteredResults = allStaleDocuments.value
    if (options.search && options.search.trim()) {
      const searchTerm = options.search.toLowerCase().trim()
      filteredResults = filteredResults.filter((doc: any) => {
        const title = (doc.title || '').toLowerCase()
        const filename = (doc.filename || '').toLowerCase()
        const caseId = (doc.case_id || '').toString().toLowerCase()
        const orgName = (doc.organization_name || '').toLowerCase()
        const orgCustomerNumber = (doc.organization_customer_number || '').toLowerCase()
        const uuid = (doc.uuid || '').toLowerCase()
        const caseUuid = (doc.case_uuid || '').toLowerCase()
        const pipelineState = getPipelineStateInfo(doc).label.toLowerCase()

        return (
          title.includes(searchTerm) ||
          filename.includes(searchTerm) ||
          caseId.includes(searchTerm) ||
          orgName.includes(searchTerm) ||
          orgCustomerNumber.includes(searchTerm) ||
          uuid.includes(searchTerm) ||
          caseUuid.includes(searchTerm) ||
          pipelineState.includes(searchTerm)
        )
      })
    }

    // Apply client-side pagination
    const startIndex = options.offset || 0
    const endIndex = startIndex + (options.limit || 20)
    const paginatedResults = filteredResults.slice(startIndex, endIndex)

    // Store current page items for select all functionality
    currentPageItems.value = paginatedResults

    return {
      results: paginatedResults,
      count: filteredResults.length,
      next: endIndex < filteredResults.length ? 'next' : null,
      previous: startIndex > 0 ? 'previous' : null
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStaleStatus = (document: TDocument) => {
    if (document.metadata_enrichment_is_finished === false) {
      return { status: 'failed', label: 'Failed', icon: AlertCircle, color: 'text-red-600' }
    }
    if (document.metadata_enrichment_is_finished === null) {
      return {
        status: 'stale',
        label: 'Stale',
        icon: AlertCircle,
        color: 'text-orange-600'
      }
    }
    return { status: 'unknown', label: 'Unknown', icon: X, color: 'text-gray-600' }
  }

  const getPipelineStateInfo = (document: TDocument) => {
    // Calculate the current pipeline state based on document fields
    const state = calculatePipelineState(document)

    const stateMap: Record<string, { label: string; color: string; icon: any }> = {
      not_started: { label: 'Not Started', color: 'text-gray-500', icon: X },
      ocr_in_progress: { label: 'OCR in Progress', color: 'text-blue-600', icon: RefreshCw },
      ocr_completed: { label: 'OCR Completed', color: 'text-green-600', icon: CheckCircle },
      ocr_failed: { label: 'OCR Failed', color: 'text-red-600', icon: AlertCircle },
      embeddings_in_progress: {
        label: 'Embeddings in Progress',
        color: 'text-blue-600',
        icon: RefreshCw
      },
      embeddings_completed: {
        label: 'Embeddings Completed',
        color: 'text-green-600',
        icon: CheckCircle
      },
      embeddings_failed: { label: 'Embeddings Failed', color: 'text-red-600', icon: AlertCircle },
      summary_in_progress: {
        label: 'Summary in Progress',
        color: 'text-blue-600',
        icon: RefreshCw
      },
      summary_completed: { label: 'Summary Completed', color: 'text-green-600', icon: CheckCircle },
      summary_failed: { label: 'Summary Failed', color: 'text-red-600', icon: AlertCircle },
      raptor_summaries_in_progress: {
        label: 'Raptor Summaries in Progress',
        color: 'text-blue-600',
        icon: RefreshCw
      },
      raptor_summaries_completed: {
        label: 'Raptor Summaries Completed',
        color: 'text-green-600',
        icon: CheckCircle
      },
      raptor_summaries_failed: {
        label: 'Raptor Summaries Failed',
        color: 'text-red-600',
        icon: AlertCircle
      },
      completed: {
        label: 'Completed',
        color: 'text-green-600',
        icon: CheckCircle
      }
    }

    return stateMap[state] || { label: 'Unknown', color: 'text-gray-500', icon: X }
  }

  const calculatePipelineState = (document: TDocument) => {
    // Check if the entire pipeline is completed (metadata enrichment indicates overall completion)
    if (
      document.metadata_enrichment_is_finished === true &&
      document.metadata_enrichment_started_at &&
      document.metadata_enrichment_finished_at &&
      !document.metadata_enrichment_error
    ) {
      return 'completed'
    }

    // Raptor summaries are the last processing stage
    if (
      document.raptor_summaries_is_finished === true &&
      document.raptor_summaries_started_at &&
      document.raptor_summaries_finished_at &&
      !document.raptor_summaries_error
    ) {
      return 'raptor_summaries_completed'
    }

    // Summary (including summary embeddings) comes before raptor summaries
    if (
      document.summary_is_finished === true &&
      document.summary_started_at &&
      document.summary_finished_at &&
      !document.summary_error &&
      document.summary_embedding_is_finished === true &&
      document.summary_embedding_started_at &&
      document.summary_embedding_finished_at &&
      !document.summary_embedding_error
    ) {
      return 'summary_completed'
    }

    // Embeddings come before summary
    if (
      document.embeddings_is_finished === true &&
      document.embeddings_started_at &&
      document.embeddings_finished_at &&
      !document.embeddings_error
    ) {
      return 'embeddings_completed'
    }

    // OCR comes before embeddings
    if (document.ocr_is_finished === true && document.ocr_started_at && document.ocr_finished_at) {
      return 'ocr_completed'
    }

    // If no stage is successfully completed, check for failed or in-progress states
    if (document.raptor_summaries_is_finished === false) {
      return 'raptor_summaries_failed'
    } else if (
      document.raptor_summaries_started_at &&
      document.raptor_summaries_is_finished === null
    ) {
      return 'raptor_summaries_in_progress'
    } else if (
      document.summary_is_finished === false ||
      document.summary_embedding_is_finished === false
    ) {
      return 'summary_failed'
    } else if (
      (document.summary_started_at && document.summary_is_finished === null) ||
      (document.summary_embedding_started_at && document.summary_embedding_is_finished === null)
    ) {
      return 'summary_in_progress'
    } else if (document.embeddings_is_finished === false) {
      return 'embeddings_failed'
    } else if (document.embeddings_started_at && document.embeddings_is_finished === null) {
      return 'embeddings_in_progress'
    } else if (document.ocr_is_finished === false) {
      return 'ocr_failed'
    } else if (document.ocr_started_at && document.ocr_is_finished === null) {
      return 'ocr_in_progress'
    } else {
      return 'not_started'
    }
  }

  const getAllPipelineStages = (document: TDocument) => {
    const stages = [
      {
        name: 'OCR',
        started: document.ocr_started_at,
        finished: document.ocr_finished_at,
        isFinished: document.ocr_is_finished,
        error: null, // OCR error field not available in API
        needs: document.needs_ocr
      },
      {
        name: 'Embeddings',
        started: document.embeddings_started_at,
        finished: document.embeddings_finished_at,
        isFinished: document.embeddings_is_finished,
        error: document.embeddings_error,
        needs: document.needs_embeddings
      },
      {
        name: 'Summary',
        started: document.summary_started_at,
        finished: document.summary_finished_at,
        isFinished: document.summary_is_finished && document.summary_embedding_is_finished,
        error: document.summary_error || document.summary_embedding_error,
        needs: document.needs_summary || document.needs_summary_embedding
      },
      {
        name: 'Raptor Summaries',
        started: document.raptor_summaries_started_at,
        finished: document.raptor_summaries_finished_at,
        isFinished: document.raptor_summaries_is_finished,
        error: document.raptor_summaries_error,
        needs: document.needs_raptor_summaries
      },
      {
        name: 'Analysis',
        started: document.analysis_started_at,
        finished: document.analysis_finished_at,
        isFinished: document.analysis_is_finished,
        error: document.analysis_error,
        needs: document.needs_analysis
      }
    ]

    return stages.map((stage) => {
      let status = 'Not Started'
      let color = 'text-gray-500'
      let icon = X

      if (stage.error) {
        status = 'Failed'
        color = 'text-red-600'
        icon = AlertCircle
      } else if (stage.isFinished === true) {
        status = 'Completed'
        color = 'text-green-600'
        icon = CheckCircle
      } else if (stage.isFinished === false) {
        status = 'Failed'
        color = 'text-red-600'
        icon = AlertCircle
      } else if (stage.started && stage.isFinished === null) {
        status = 'In Progress'
        color = 'text-blue-600'
        icon = RefreshCw
      } else if (!stage.needs) {
        status = 'Skipped'
        color = 'text-gray-400'
        icon = X
      }

      return {
        ...stage,
        status,
        color,
        icon
      }
    })
  }

  const handleDownload = async (doc: TDocument) => {
    try {
      const downloadUrl = doc.current_pdf_url || doc.original_pdf_url
      if (downloadUrl) {
        const filename = doc.filename || doc.title || 'document.pdf'
        downloadDocument(downloadUrl, filename)

        toast({
          title: 'Download Started',
          description: 'Document download has been initiated.',
          variant: 'success'
        })
      } else {
        toast({
          title: 'Download Unavailable',
          description: 'No download URL available for this document.',
          variant: 'destructive'
        })
      }
    } catch (error) {
      console.error('Download error:', error)
      toast({
        title: 'Download Error',
        description: 'Failed to download document.',
        variant: 'destructive'
      })
    }
  }

  const clearFilters = () => {
    timeRangeFilter.value = '24'
    orgFilter.value = 'all'
    staleThresholdMinutes.value = '15'
    pageSize.value = '20'
    showErrored.value = 'false'
    orgSearchTerm.value = ''
    // Clear cached data and trigger refresh
    allStaleDocuments.value = []
    isLoadingData.value = true

    if (paginatedTableRef.value) {
      paginatedTableRef.value.refreshData()
    }
  }

  const loadOrganizations = async () => {
    try {
      const result = await OrganisationsService.organisationsList({ limit: 1000 })
      organizations.value = result.results || []
    } catch (error) {
      console.error('Error loading organizations:', error)
      toast({
        title: 'Error',
        description: 'Failed to load organizations',
        variant: 'destructive'
      })
    }
  }

  const searchOrganizations = (event: Event) => {
    const target = event.target as HTMLInputElement
    orgSearchTerm.value = target.value
  }

  const filteredOrganizations = computed(() => {
    if (!orgSearchTerm.value || typeof orgSearchTerm.value !== 'string') return organizations.value
    return organizations.value.filter((org) =>
      org.name?.toLowerCase().includes(orgSearchTerm.value.toLowerCase())
    )
  })

  const copyUuid = async (uuid: string) => {
    try {
      await navigator.clipboard.writeText(uuid)
      toast({
        title: 'Success',
        description: 'UUID copied to clipboard',
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy UUID',
        variant: 'destructive'
      })
    }
  }

  const copyError = async (error: string) => {
    try {
      await navigator.clipboard.writeText(error)
      toast({
        title: 'Success',
        description: 'Error message copied to clipboard',
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy error message',
        variant: 'destructive'
      })
    }
  }

  const openDocumentApiUrl = (uuid: string) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'https://app.jupus.de'
    // Remove trailing slash and /api if present, then add the correct path
    const cleanBaseUrl = baseUrl.replace(/\/api\/?$/, '').replace(/\/$/, '')
    const apiUrl = `${cleanBaseUrl}/api/v3/documents/${uuid}/`
    window.open(apiUrl, '_blank', 'noopener,noreferrer')
  }

  const openDocumentPdf = (document: TDocument) => {
    const pdfUrl = document.current_pdf_url || document.original_pdf_url
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer')
    } else {
      toast({
        title: 'Error',
        description: 'No PDF available for this document',
        variant: 'destructive'
      })
    }
  }

  // Selection functions
  const toggleSelectAll = () => {
    if (isSelectAll.value) {
      // Deselect all current page items
      currentPageItems.value.forEach((item) => {
        selectedDocuments.value.delete(item.uuid)
      })
      isSelectAll.value = false
    } else {
      // Select all current page items
      currentPageItems.value.forEach((item) => {
        selectedDocuments.value.add(item.uuid)
      })
      isSelectAll.value = true
    }
  }

  const toggleSelectDocument = (uuid: string) => {
    if (selectedDocuments.value.has(uuid)) {
      selectedDocuments.value.delete(uuid)
    } else {
      selectedDocuments.value.add(uuid)
    }

    // Update select all state based on current page selection
    const currentPageUuids = currentPageItems.value.map((item) => item.uuid)
    const selectedCurrentPage = currentPageUuids.filter((uuid) => selectedDocuments.value.has(uuid))
    isSelectAll.value =
      selectedCurrentPage.length === currentPageUuids.length && currentPageUuids.length > 0
  }

  const isDocumentSelected = (uuid: string) => {
    return selectedDocuments.value.has(uuid)
  }

  const clearSelection = () => {
    selectedDocuments.value.clear()
    isSelectAll.value = false
  }

  // Computed properties
  const selectedCount = computed(() => selectedDocuments.value.size)
  const hasSelection = computed(() => selectedCount.value > 0)

  const fetchOptions = computed(() => ({
    limit: parseInt(pageSize.value)
  }))

  // Confirmation dialog functions
  const showConfirmation = (title: string, description: string, action: () => Promise<void>) => {
    confirmTitle.value = title
    confirmDescription.value = description
    confirmAction.value = action
    showConfirmDialog.value = true
  }

  const executeConfirmedAction = async () => {
    await confirmAction.value()
    showConfirmDialog.value = false
  }

  // Bulk action functions
  const bulkMarkAsFailed = () => {
    if (!hasSelection.value) return

    showConfirmation(
      'Mark Documents as Failed',
      `Mark ${selectedCount.value} document${selectedCount.value > 1 ? 's' : ''} as failed. This indicates that AI processing has failed and users will see that the document processing was unsuccessful. Users will have the ability to manually retry processing from the application.`,
      async () => {
        try {
          const promises = Array.from(selectedDocuments.value).map((uuid) =>
            StaleDocumentsService.staleDocumentsMarkFailedCreate({ uuid })
          )
          await Promise.all(promises)

          toast({
            title: 'Success',
            description: `${selectedCount.value} documents marked as failed`,
            variant: 'default'
          })

          clearSelection()
          if (paginatedTableRef.value) {
            await paginatedTableRef.value.refreshData()
          }
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to mark documents as failed',
            variant: 'destructive'
          })
        }
      }
    )
  }

  const bulkMarkAsCompleted = () => {
    if (!hasSelection.value) return

    showConfirmation(
      'Mark Documents as Completed',
      `Mark ${selectedCount.value} document${selectedCount.value > 1 ? 's' : ''} as completed. This indicates that the document has been successfully processed by AI and users will see that the document processing was successful.`,
      async () => {
        try {
          const promises = Array.from(selectedDocuments.value).map((uuid) =>
            StaleDocumentsService.staleDocumentsMarkCompletedCreate({ uuid })
          )
          await Promise.all(promises)

          toast({
            title: 'Success',
            description: `${selectedCount.value} documents marked as completed`,
            variant: 'default'
          })

          clearSelection()
          if (paginatedTableRef.value) {
            await paginatedTableRef.value.refreshData()
          }
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to mark documents as completed',
            variant: 'destructive'
          })
        }
      }
    )
  }

  const bulkRetryProcessing = () => {
    if (!hasSelection.value) return

    showConfirmation(
      'Retry Document Processing',
      `Retry AI processing for ${selectedCount.value} document${selectedCount.value > 1 ? 's' : ''}. This will restart the AI pipeline so that AI can harness the knowledge of the document and process it again.`,
      async () => {
        try {
          const promises = Array.from(selectedDocuments.value).map((uuid) =>
            StaleDocumentsService.staleDocumentsRetryProcessingCreate({ uuid })
          )
          await Promise.all(promises)

          toast({
            title: 'Success',
            description: `${selectedCount.value} documents queued for retry`,
            variant: 'default'
          })

          clearSelection()
          if (paginatedTableRef.value) {
            await paginatedTableRef.value.refreshData()
          }
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to retry document processing',
            variant: 'destructive'
          })
        }
      }
    )
  }

  // Watch for page size changes to refresh the table
  watch(pageSize, () => {
    if (paginatedTableRef.value) {
      paginatedTableRef.value.refreshData()
    }
  })

  // Apply filters function
  const applyFilters = () => {
    // Clear cached data to force fresh fetch
    allStaleDocuments.value = []
    isLoadingData.value = true

    if (paginatedTableRef.value) {
      paginatedTableRef.value.refreshData()
    }
  }

  onMounted(async () => {
    await loadOrganizations()
  })
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Documents', to: ROUTES.ADMIN.DOCUMENTS.DOCUMENTS_LIST },
            { title: 'Stale Documents', to: ROUTES.ADMIN.DOCUMENTS.STALE_DOCUMENTS }
          ]"
        />
      </div>

      <!-- Filters -->
      <div
        class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
      >
        <h3 class="mb-4 text-lg font-semibold">Filters</h3>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
          <!-- Time Range -->
          <div class="space-y-2">
            <Label for="time-range">Time Range</Label>
            <Select v-model="timeRangeFilter">
              <SelectTrigger>
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24">Last 24 hours</SelectItem>
                <SelectItem value="48">Last 48 hours</SelectItem>
                <SelectItem value="72">Last 72 hours</SelectItem>
                <SelectItem value="168">Last week</SelectItem>
                <SelectItem value="720">Last month</SelectItem>
                <SelectItem value="4320">Last 6 months</SelectItem>
                <SelectItem value="8760">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <!-- Organization Filter -->
          <div class="space-y-2">
            <Label for="org">Organization</Label>
            <Select v-model="orgFilter">
              <SelectTrigger>
                <SelectValue placeholder="All organizations" />
              </SelectTrigger>
              <SelectContent>
                <div class="p-2">
                  <Input
                    placeholder="Search organizations..."
                    class="mb-2"
                    @input="searchOrganizations"
                  />
                </div>
                <SelectItem value="all">All organizations</SelectItem>
                <SelectItem
                  v-for="org in filteredOrganizations"
                  :key="org.uuid"
                  :value="org.uuid"
                >
                  {{ org.name }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <!-- Document Status -->
          <div class="space-y-2">
            <Label for="show-errored">Document Status</Label>
            <Select v-model="showErrored">
              <SelectTrigger>
                <SelectValue placeholder="Select document status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">Stale Documents</SelectItem>
                <SelectItem value="true">Errored Documents</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <!-- Stale Threshold -->
          <div
            v-if="showErrored !== 'true'"
            class="space-y-2"
          >
            <Label for="stale-threshold">Stale Threshold (minutes)</Label>
            <Input
              v-model="staleThresholdMinutes"
              type="number"
              min="1"
              max="1440"
              placeholder="15"
            />
          </div>

          <!-- Page Size -->
          <div class="space-y-2">
            <Label for="page-size">Results per page</Label>
            <Select v-model="pageSize">
              <SelectTrigger>
                <SelectValue placeholder="Select page size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div class="mt-4 flex gap-2">
          <Button @click="applyFilters">
            <Search class="mr-2 h-4 w-4" />
            Apply Filters
          </Button>
          <Button
            variant="secondary"
            @click="clearFilters"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      <!-- Stale Documents Table -->
      <div
        class="relative rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <!-- Loading Overlay -->
        <div
          v-if="isLoadingData"
          class="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/80 backdrop-blur-sm
            dark:bg-neutral-800/80"
        >
          <div class="flex flex-col items-center gap-3">
            <RefreshCw class="h-8 w-8 animate-spin text-blue-600" />
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Loading documents...</p>
          </div>
        </div>

        <PaginatedTableView
          ref="paginatedTableRef"
          :fetchData="fetchStaleDocuments"
          :fetchOptions="fetchOptions"
          searchableFields="document name, filename, case ID, organization, customer number, document UUID, case UUID, pipeline state"
        >
          <template #actions>
            <div class="flex items-center gap-2">
              <span
                v-if="hasSelection"
                class="text-sm text-gray-600"
              >
                {{ selectedCount }} selected
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <Button
                      size="sm"
                      variant="secondary"
                      :disabled="!hasSelection"
                      @click="bulkMarkAsFailed"
                    >
                      <X class="mr-1 h-4 w-4" />
                      Mark Failed
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Select rows first to mark as failed (indicates AI processing failed)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <Button
                      size="sm"
                      variant="secondary"
                      :disabled="!hasSelection"
                      @click="bulkMarkAsCompleted"
                    >
                      <CheckCircle class="mr-1 h-4 w-4" />
                      Mark Completed
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Select rows first to mark as completed (indicates AI processing successful)
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <Button
                      size="sm"
                      variant="secondary"
                      :disabled="!hasSelection"
                      @click="bulkRetryProcessing"
                    >
                      <RefreshCw class="mr-1 h-4 w-4" />
                      Retry
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Select rows first to retry AI processing (restarts AI pipeline)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                v-if="hasSelection"
                size="sm"
                variant="ghost"
                @click="clearSelection"
              >
                Clear
              </Button>
            </div>
          </template>
          <template #table-head>
            <TableCell>
              <Checkbox
                :checked="isSelectAll"
                @update:checked="toggleSelectAll"
              />
            </TableCell>
            <TableCell>Document Name</TableCell>
            <TableCell>UUID</TableCell>
            <TableCell>Case</TableCell>
            <TableCell>Case UUID</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell>Customer Number</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Pipeline State</TableCell>
            <TableCell>Started At</TableCell>
            <TableCell>Error</TableCell>
            <TableCell>Actions</TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="document in items"
              :key="document.uuid"
            >
              <TableCell>
                <Checkbox
                  :checked="isDocumentSelected(document.uuid)"
                  @update:checked="toggleSelectDocument(document.uuid)"
                />
              </TableCell>
              <TableCell>
                <div
                  class="cursor-pointer truncate text-blue-600 hover:text-blue-800 hover:underline"
                  :title="`${document.title} - Click to open PDF`"
                  @click="openDocumentPdf(document)"
                >
                  {{ document.title }}
                </div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-2">
                  <code
                    class="cursor-pointer rounded bg-gray-100 px-2 py-1 font-mono text-xs text-blue-600 hover:text-blue-800
                      hover:underline"
                    :title="`${document.uuid} - Click to open API URL`"
                    @click="openDocumentApiUrl(document.uuid)"
                  >
                    {{ document.uuid }}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    class="h-6 w-6 p-0"
                    @click="copyUuid(document.uuid)"
                  >
                    <Copy class="h-3 w-3" />
                  </Button>
                </div>
              </TableCell>
              <TableCell>
                <div
                  v-if="document.case_id"
                  class="flex items-center gap-2"
                >
                  <code class="rounded bg-gray-100 px-2 py-1 font-mono text-xs">
                    {{ document.case_id }}
                  </code>
                </div>
                <div
                  v-else
                  class="text-gray-500"
                >
                  No case
                </div>
              </TableCell>
              <TableCell>
                <div
                  v-if="document.case_uuid"
                  class="flex items-center gap-2"
                >
                  <code class="rounded bg-gray-100 px-2 py-1 font-mono text-xs">
                    {{ document.case_uuid }}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    class="h-6 w-6 p-0"
                    @click="copyUuid(document.case_uuid)"
                  >
                    <Copy class="h-3 w-3" />
                  </Button>
                </div>
                <div
                  v-else
                  class="text-gray-500"
                >
                  No case
                </div>
              </TableCell>
              <TableCell>
                <div
                  v-if="document.organization_name"
                  class="truncate"
                >
                  {{ document.organization_name }}
                </div>
                <div
                  v-else
                  class="text-gray-500"
                >
                  No organization
                </div>
              </TableCell>
              <TableCell>
                <div
                  v-if="document.organization_customer_number"
                  class="flex items-center gap-2"
                >
                  <code class="rounded bg-gray-100 px-2 py-1 font-mono text-xs">
                    {{ document.organization_customer_number }}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    class="h-6 w-6 p-0"
                    @click="copyUuid(document.organization_customer_number)"
                  >
                    <Copy class="h-3 w-3" />
                  </Button>
                </div>
                <div
                  v-else
                  class="text-gray-500"
                >
                  No organization
                </div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-2">
                  <component
                    :is="getStaleStatus(document).icon"
                    :class="getStaleStatus(document).color"
                    class="h-4 w-4"
                  />
                  <span :class="getStaleStatus(document).color">
                    {{ getStaleStatus(document).label }}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <div class="flex cursor-help items-center gap-2">
                        <component
                          :is="getPipelineStateInfo(document).icon"
                          :class="getPipelineStateInfo(document).color"
                          class="h-4 w-4"
                        />
                        <span :class="getPipelineStateInfo(document).color">
                          {{ getPipelineStateInfo(document).label }}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent class="max-w-md">
                      <div class="space-y-2">
                        <div class="text-sm font-semibold">Pipeline Stages</div>
                        <div class="space-y-1">
                          <div
                            v-for="stage in getAllPipelineStages(document)"
                            :key="stage.name"
                            class="flex items-center justify-between gap-2 text-xs"
                          >
                            <span class="font-medium">{{ stage.name }}</span>
                            <div class="flex items-center gap-1">
                              <component
                                :is="stage.icon"
                                :class="stage.color"
                                class="h-3 w-3"
                              />
                              <span :class="stage.color">{{ stage.status }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
              <TableCell>
                {{
                  formatDate(
                    document.metadata_enrichment_started_at || document.ocr_started_at || ''
                  )
                }}
              </TableCell>
              <TableCell>
                <div
                  v-if="document.metadata_enrichment_error"
                  class="flex items-center gap-2"
                >
                  <div
                    class="max-w-xs truncate text-red-600"
                    :title="document.metadata_enrichment_error"
                  >
                    {{ document.metadata_enrichment_error }}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    class="h-6 w-6 p-0"
                    @click="copyError(document.metadata_enrichment_error)"
                  >
                    <Copy class="h-3 w-3" />
                  </Button>
                </div>
                <div
                  v-else
                  class="text-gray-500"
                >
                  No error
                </div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <Button
                          size="sm"
                          variant="ghost"
                          :disabled="!document.current_pdf_url && !document.original_pdf_url"
                          class="h-8 w-8 p-0"
                          @click="handleDownload(document)"
                        >
                          <Download class="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Download PDF</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>

      <!-- Confirmation Dialog -->
      <Dialog v-model:open="showConfirmDialog">
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{{ confirmTitle }}</DialogTitle>
            <DialogDescription>
              {{ confirmDescription }}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              @click="showConfirmDialog = false"
            >
              Cancel
            </Button>
            <Button @click="executeConfirmedAction">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  </div>
</template>

<style scoped>
  .truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
